import styled from '@emotion/styled'
import { CartItem } from '../../../types'
import {
    getCurrentSubscriptionOption,
    getDefaultSubscriptionOption,
    getValidSellingPlanAllocations,
    getSubscriptionText,
    useSubscriptionOptions,
} from '../../../utils/subscriptions'
import { useContext, useMemo, useState } from 'react'
import { CartContext } from '../../../context/cartContext'
import {
    BUTTON_BORDER_RADIUS_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_CTA_FILL_COLOR_VAR,
    SECONDARY_CTA_TEXT_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import {
    hoverInvertOutline,
    hoverTintSurface,
} from '../../../style/interactiveStates'
import { getTypographyRoleStyle } from '../../../style/typography'
import { scaledFontSize } from '../../../style/scrollingLayout'

const SubscriptionOptionSelector = styled.select({
    display: 'flex',
    background: 'none',
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    border: `1px solid var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    ...hoverTintSurface(),
    padding: '10px',
    borderRadius: '0.2rem',
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    backgroundSize: '16px',
    width: '100%',
    ...getTypographyRoleStyle('labelText'),
})

const SubscriptionOptionDivider = styled.hr({
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
})

/** Hollow by default, so its outline becomes the fill rather than darkening. */
const SubscriptionButtonComponent = styled.button({
    border: `1px solid var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    padding: '10px 10px 10px 10px',
    borderRadius: `var(${BUTTON_BORDER_RADIUS_VAR})`,
    cursor: 'pointer',
    ...hoverInvertOutline(
        `var(${SECONDARY_CTA_FILL_COLOR_VAR}, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}))`,
        `var(${SECONDARY_CTA_TEXT_COLOR_VAR}, var(${PRIMARY_PAGE_FILL_COLOR_VAR}))`,
    ),
    ...getTypographyRoleStyle('buttonText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(13),
    height: 35,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
})

const SubscriptionButton: React.FC<{
    cartItem: CartItem
    onClick: () => void
    disabled?: boolean
}> = ({ cartItem, disabled, onClick }) => {
    const subscriptionOptions = useSubscriptionOptions()
    const subscriptionText =
        subscriptionOptions?.subscriptionButtonText ??
        getSubscriptionText(cartItem, subscriptionOptions)

    return (
        <SubscriptionButtonComponent
            onClick={onClick}
            disabled={disabled}
            style={{
                background: `var(${SECONDARY_CTA_FILL_COLOR_VAR})`,
                color: `var(${SECONDARY_CTA_TEXT_COLOR_VAR})`,
                border: `1px solid var(${SECONDARY_CTA_TEXT_COLOR_VAR})`,
            }}
        >
            {subscriptionText}
        </SubscriptionButtonComponent>
    )
}

const ONE_TIME_PURCHASE_OPTION = 'one-time-purchase'

export const CartSubscription: React.FC<{
    cartItem: CartItem
    itemNotReady?: boolean
}> = ({ cartItem, itemNotReady }) => {
    const subscriptionOptions = useSubscriptionOptions()
    const currentSubscriptionOption = getCurrentSubscriptionOption(
        cartItem,
        subscriptionOptions,
    )
    const [previousSubscriptionOption, setPreviousSubscriptionOption] =
        useState(currentSubscriptionOption)

    // Makes the button or selector "sticky" while item is transitioning
    // between adding a new subscription or reverting back to a one-time-purchase.
    // itemNotReady = true means the item is being altered.
    const showSubscriptionButton = useMemo(() => {
        if (itemNotReady) {
            return !previousSubscriptionOption
        }
        return !currentSubscriptionOption
    }, [itemNotReady, previousSubscriptionOption, currentSubscriptionOption])

    // In conjunction with the above, do not revert the UI element if the
    // item is being alterted by a non-subscription action (eg: delete or update quantity).
    if (
        !itemNotReady &&
        previousSubscriptionOption !== currentSubscriptionOption
    ) {
        setPreviousSubscriptionOption(currentSubscriptionOption)
    }

    const sellingPlanOptions = getValidSellingPlanAllocations(
        cartItem,
        subscriptionOptions,
    )
    const { updateCartItem } = useContext(CartContext)

    if (showSubscriptionButton) {
        return (
            <SubscriptionButton
                cartItem={cartItem}
                disabled={itemNotReady}
                onClick={() => {
                    setPreviousSubscriptionOption(currentSubscriptionOption)
                    updateCartItem(
                        cartItem.variant,
                        undefined,
                        getDefaultSubscriptionOption(
                            cartItem,
                            subscriptionOptions,
                        )?.sellingPlan.id,
                        false,
                        undefined,
                        cartItem.sellingPlanId ?? null,
                    )
                }}
            />
        )
    }
    return (
        <SubscriptionOptionSelector
            disabled={itemNotReady}
            onChange={(e) => {
                setPreviousSubscriptionOption(currentSubscriptionOption)
                updateCartItem(
                    cartItem.variant,
                    undefined,
                    e.target.value === ONE_TIME_PURCHASE_OPTION
                        ? null
                        : e.target.value,
                    false,
                    undefined,
                    cartItem.sellingPlanId ?? null,
                )
            }}
            value={
                currentSubscriptionOption?.sellingPlan.id ??
                ONE_TIME_PURCHASE_OPTION
            }
        >
            <option
                key={cartItem.shopifyLineId + ONE_TIME_PURCHASE_OPTION}
                value={ONE_TIME_PURCHASE_OPTION}
            >
                One-time purchase
            </option>
            <SubscriptionOptionDivider />
            {sellingPlanOptions.map((option) => (
                <option
                    key={option.sellingPlan.id}
                    value={option.sellingPlan.id}
                >
                    {option.sellingPlan.name}
                </option>
            ))}
        </SubscriptionOptionSelector>
    )
}
